import { formatTime, Toast } from "../Utils";
import { Button, Empty } from "../Components/Base";
import { ProfileLayout } from "../Container/Layout";
import React, {
  FormEventHandler,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LoadingContext, MainContext } from "../Context";
import {
  TransactionDeleteSans,
  TransactionGetCoach,
  TransactionUpdateCoach,
} from "../Services/Transactions";
import { CoachImageType, CoachSansType } from "../Structures";
import { ConfirmModal, CreateSansModal } from "../Components/Modal";
import { CoachGallery } from "../Components/Pages";

const CoachInformationPage = () => {
  const { setLoading } = useContext(LoadingContext);
  const { townList } = useContext(MainContext);

  // ==========================================================================================

  const [gymName, setGymName] = useState<string>("");
  const [gymAddress, setGymAddress] = useState<string>("");
  const [town_id, setTown_id] = useState<number>(0);
  const [resume, setResume] = useState<File | null>(null);
  const [uploadedResume, setUploadedResume] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddSansModal, setShowAddSansModal] = useState<boolean>(false);
  const [imagesList, setImagesList] = useState<CoachImageType[]>([]);
  const [premiumExpireDate, setPremiumExpireDate] = useState<string>("");

  const [sansList, setSansList] = useState<CoachSansType[]>([]);
  const [selectedSansForDelete, setSelectedSansForDelete] =
    useState<CoachSansType["id"]>(0);

  // ==========================================================================================

  const handleSubmitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (formIsValid()) {
      const form = new FormData();
      form.append("gymName", gymName!);
      form.append("gymAddress", gymAddress!);
      form.append("town_id", town_id.toString());
      if (resume) {
        form.append("resume", resume);
      }
      setIsLoading(true);
      TransactionUpdateCoach(form)
        .then(({ data: res }) => {
          if (res.transaction_status) {
            Toast("success", res.message);
            getInfo();
          } else {
            Toast("error", res.message);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleDeleteSans = () => {
    setLoading(true);
    TransactionDeleteSans(selectedSansForDelete)
      .then(({ data: res }) => {
        if (res.transaction_status) {
          setSansList(
            sansList.filter((sans) => sans.id != selectedSansForDelete),
          );
          setSelectedSansForDelete(0);
          Toast("success", res.message);
        } else {
          Toast("error", res.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const getInfo = () => {
    setLoading(true);
    TransactionGetCoach("0")
      .then(({ data: res }) => {
        if (res.transaction_status) {
          if (res.data) {
            const {
              gymAddress,
              gymName,
              town,
              sans,
              images,
              resume,
              premiumExpireDate,
            } = res.data;
            setGymAddress(gymAddress);
            setGymName(gymName);
            setTown_id(town.id);
            setPremiumExpireDate(premiumExpireDate);
            setSansList(sans.reverse());
            setUploadedResume(resume || "");
            setImagesList(images);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  // ==========================================================================================

  const formIsValid = useCallback(() => {
    return gymName && town_id && gymAddress;
  }, [gymName, town_id, gymAddress]);

  // ==========================================================================================

  useEffect(() => {
    getInfo();
  }, []);

  // ==========================================================================================
  return (
    <ProfileLayout>
      <div className="col-12">
        <div className="card border mb-4">
          <div className="card-header border-bottom p-3">
            <h4 className="card-header-title mb-0">اطلاعات</h4>
          </div>
          <form onSubmit={handleSubmitForm} className="card-body">
            <div className="alert alert-warning">
              تاریخ پایان اشتراک : <strong>{premiumExpireDate}</strong>
            </div>
            <div className="mb-3">
              <label className="form-label">نام باشگاه</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={gymName}
                  onChange={(e) => setGymName(e.target.value)}
                  placeholder="نام باشگاه خود را وارد کنید"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">آدرس باشگاه</label>
              <textarea
                value={gymAddress}
                onChange={(e) => setGymAddress(e.target.value)}
                className="form-control resize-none"
                rows={4}
              >
                {gymAddress}
              </textarea>
              <div className="form-text">آدرس باشگاه خود را وارد کنید.</div>
            </div>

            <div className="mb-3">
              <label className="form-label">استان</label>
              <select
                onChange={(e) => setTown_id(Number(e.target.value))}
                className="form-select cursor-pointer"
                value={town_id}
              >
                <option value={0}>انتخاب کنید</option>
                {townList.map((town) => (
                  <option key={town.id} value={town.id}>
                    {town.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label htmlFor="formFile" className="form-label">
                فایل رزومه ( PDF, JPEG, JPG, PNG )
              </label>
              <input
                accept=".pdf,.jpeg,.jpg,.png"
                className="form-control"
                type="file"
                onChange={(e) => setResume(e.target.files?.[0] || null)}
                id="formFile"
              />
            </div>
            {uploadedResume ? (
              <div className="mb-3">
                <div className="alert py-2 mx-0 flex-wrap flex-md-nowrap mt-3 py-1 justify-content-between alert-info d-flex align-items-center">
                  <div className="w-100 mb-2 mb-md-0 mt-2 mt-sm-0 d-flex align-items-center justify-content-start">
                    <i className="fa fa-info-circle text-info me-2 "></i>
                    در حال حاضر رزومه‌ای آپلود کرده اید:
                    <a
                      className="fw-bold mx-2"
                      target="_blank"
                      href={uploadedResume}
                      download={true}
                    >
                      {uploadedResume.split("/").at(-1)}
                    </a>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="d-flex w-100 justify-content-end mt-4">
              <Button
                text="ثبت تغییرات"
                disabled={!formIsValid()}
                isLoading={isLoading}
                btnClass="btn w-100 btn-primary"
              />
            </div>
          </form>
        </div>
        <div className="card border mb-4">
          <div className="card-header border-bottom d-flex align-items-center justify-content-between p-3">
            <h4 className="card-header-title mb-0">سانس ها</h4>
            <Button
              btnClass="btn-primary w-25px h-25 mb-0"
              onClick={() => setShowAddSansModal(true)}
            >
              <i className="fa fa-plus-circle" />
            </Button>
          </div>
          <Empty
            data={sansList}
            isLoading={false}
            description="در حال حاضر سانسی ایجاد نکردید"
          >
            <div className="card-body">
              <div className="d-flex w-100 justify-content-end mt-4">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <td scope="col">
                        <span className="mx-2">شناسه</span>
                      </td>
                      <td scope="col">روز</td>
                      <td scope="col">ساعت شروع</td>
                      <td scope="col">ساعت پایان</td>
                    </tr>
                  </thead>
                  <tbody>
                    {sansList.map((sans) => (
                      <tr
                        style={{ height: 50, verticalAlign: "middle" }}
                        key={sans.id}
                      >
                        <td className="mx-3">
                          <span className="mx-2">{sans.id}</span>
                        </td>
                        <td>{sans.day}</td>
                        <td>{formatTime(sans.start_time)}</td>
                        <td>{formatTime(sans.end_time)}</td>
                        <td>
                          <span
                            onClick={() => setSelectedSansForDelete(sans.id)}
                            className="d-flex w-100 justify-content-center align-items-center"
                          >
                            <i className="bi bi-trash text-danger fs-5 d-flex cursor-pointer" />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Empty>
        </div>
        <CoachGallery onSubmit={() => getInfo()} imagesList={imagesList} />
        {selectedSansForDelete ? (
          <ConfirmModal
            onClose={() => setSelectedSansForDelete(0)}
            onSubmit={handleDeleteSans}
          />
        ) : null}
        {showAddSansModal ? (
          <CreateSansModal
            onClose={() => setShowAddSansModal(false)}
            onSubmit={() => getInfo()}
          />
        ) : null}
      </div>
    </ProfileLayout>
  );
};

export default CoachInformationPage;
